import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import './ObjektReport.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Formatierung ──
function eur(val) {
  if (val == null || isNaN(val)) return '\u2014';
  return Number(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20ac';
}

// ── Objekt-Selektor ──
function ObjektSelector({ token, selected, onSelect, disabled }) {
  const [objekte, setObjekte] = useState([]);
  const [search, setSearch] = useState('');
  const [filterArt, setFilterArt] = useState('alle');

  useEffect(() => {
    fetch(`${API_URL}/admin/objekte/aktiv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.objekte) setObjekte(data.objekte); })
      .catch(() => {});
  }, [token]);

  const verwaltungsarten = useMemo(() => {
    return [...new Set(objekte.map(o => o.verwaltungsart).filter(Boolean))].sort();
  }, [objekte]);

  const filtered = useMemo(() => {
    return objekte.filter(o => {
      if (filterArt !== 'alle' && o.verwaltungsart !== filterArt) return false;
      if (search) {
        const s = search.toLowerCase();
        return o.objekt_nr.includes(s) || (o.adresse || '').toLowerCase().includes(s);
      }
      return true;
    });
  }, [objekte, filterArt, search]);

  const vartClass = art => {
    if (art === 'Mietverwaltung') return 'objreport__vart-badge objreport__vart-badge--miete';
    if (art === 'WEG-Verwaltung' || art === 'WEG mit SE-Verwaltung') return 'objreport__vart-badge objreport__vart-badge--weg';
    return 'objreport__vart-badge objreport__vart-badge--default';
  };

  const vartLabel = art => {
    if (art === 'Mietverwaltung') return 'Miete';
    if (art === 'WEG-Verwaltung') return 'WEG';
    if (art === 'WEG mit SE-Verwaltung') return 'WEG+SE';
    return art || '-';
  };

  return (
    <div>
      <div className="objreport__selector-row">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suche nach Nr. oder Adresse..."
          disabled={disabled}
          className="objreport__selector-input"
        />
        <select value={filterArt} onChange={e => setFilterArt(e.target.value)} disabled={disabled}
          className="objreport__selector-select">
          <option value="alle">Alle Verwaltungsarten</option>
          {verwaltungsarten.map(art => <option key={art} value={art}>{art}</option>)}
        </select>
      </div>
      <div className="objreport__selector-table-wrap">
        <table className="objreport__selector-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}></th>
              <th>Nr.</th>
              <th>Adresse</th>
              <th>Art</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.immoware_id}
                onClick={() => !disabled && onSelect(o.immoware_id)}
                className={selected === o.immoware_id ? '--selected' : ''}
              >
                <td style={{ textAlign: 'center' }}>
                  <input type="radio" checked={selected === o.immoware_id} readOnly disabled={disabled} />
                </td>
                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{o.objekt_nr}</td>
                <td style={{ color: 'var(--text-muted)' }}>{o.adresse || '-'}</td>
                <td><span className={vartClass(o.verwaltungsart)}>{vartLabel(o.verwaltungsart)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="objreport__selector-selected">
          <span>&#10003;</span> Ausgew&auml;hlt: {objekte.find(o => o.immoware_id === selected)?.objekt_nr || selected}
        </div>
      )}
    </div>
  );
}

// ── Section Card ──
function Section({ icon, title, badge, children }) {
  return (
    <div className="objreport__section">
      <div className="objreport__section-head">
        {icon && <span className="objreport__section-icon">{icon}</span>}
        <h3 className="objreport__section-title">{title}</h3>
        {badge && <span className="objreport__section-badge">{badge}</span>}
      </div>
      <div className="objreport__section-body">{children}</div>
    </div>
  );
}

// ── Konten-Tabelle ──
function KontenTabelle({ konten, summe, summeLabel, showZugang, showAbgang }) {
  if (!konten || konten.length === 0) return null;
  const hasZugang = showZugang || konten.some(k => k.zugang);
  const hasAbgang = showAbgang || konten.some(k => k.abgang);
  return (
    <table className="objreport__table">
      <thead>
        <tr>
          <th>Konto</th>
          <th>Bezeichnung</th>
          {hasZugang && <th className="objreport__num">Zugang</th>}
          {hasAbgang && <th className="objreport__num">Abgang</th>}
          <th className="objreport__num">Saldo</th>
        </tr>
      </thead>
      <tbody>
        {konten.map((k, i) => (
          <tr key={i}>
            <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{k.konto || '\u2014'}</td>
            <td>{k.name || k.bezeichnung || '\u2014'}</td>
            {hasZugang && <td className="objreport__num">{k.zugang ? eur(k.zugang) : ''}</td>}
            {hasAbgang && <td className="objreport__num">{k.abgang ? eur(k.abgang) : ''}</td>}
            <td className="objreport__num" style={{ fontWeight: 600 }}>{eur(k.saldo)}</td>
          </tr>
        ))}
      </tbody>
      {summe != null && (
        <tfoot>
          <tr>
            <td colSpan={1 + 1 + (hasZugang ? 1 : 0) + (hasAbgang ? 1 : 0)}>{summeLabel || 'Summe'}</td>
            <td className="objreport__num">{eur(summe)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

// ── Wasserfall-Bar ──
function WasserfallBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min(Math.abs(value) / max * 100, 100) : 0;
  return (
    <div className="objreport__wf-row">
      <span className="objreport__wf-label">{label}</span>
      <div className="objreport__wf-track">
        <div className="objreport__wf-fill" style={{ width: `${pct}%`, background: color || 'var(--accent)' }} />
      </div>
      <span className="objreport__wf-val">{eur(value)}</span>
    </div>
  );
}

// ── Loading Overlay ──
function LoadingOverlay({ message }) {
  return (
    <div className="objreport__loading">
      <div className="objreport__loading-spinner" />
      <div className="objreport__loading-title">Report wird geladen...</div>
      <div className="objreport__loading-desc">{message || 'Login bei Immoware und Datenabfrage. Das kann 30-60 Sekunden dauern.'}</div>
      <div className="objreport__loading-bar">
        <div className="objreport__loading-bar-fill" />
      </div>
    </div>
  );
}

// ── Hauptkomponente ──
export default function ObjektReport() {
  const { token } = useAuth();
  const [selectedObjekt, setSelectedObjekt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadReport = async () => {
    if (!selectedObjekt) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const resp = await fetch(`${API_URL}/skills/objekt-report/${selectedObjekt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => null);
        throw new Error(errBody?.detail || `Fehler ${resp.status}`);
      }
      setData(await resp.json());
    } catch (e) {
      setError(e.message || 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  const downloadHtmlReport = () => {
    if (!data || !selectedObjekt) return;
    fetch(API_URL + '/skills/objekt-report/' + selectedObjekt + '/html', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Eigentuemer-Report.html';
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  // ── Daten-Extraktion ──
  const d = data || {};
  const meta = d.meta || {};
  const zm = d.zahlungen_mieter || {};
  const zmGesamt = zm.gesamt || {};
  const euer = d.euer || {};
  const einnahmen = Array.isArray(d.einnahmen) ? d.einnahmen : [];
  const ausgabenUmlage = Array.isArray(d.ausgaben_umlagefaehig) ? d.ausgaben_umlagefaehig : [];
  const ausgabenNichtUmlage = Array.isArray(d.ausgaben_nicht_umlagefaehig) ? d.ausgaben_nicht_umlagefaehig : [];
  const darlehen = Array.isArray(d.darlehen) ? d.darlehen : [];
  const banksaldo = d.banksaldo || {};
  const entnahmen = d.entnahmen_einlagen || {};
  const durchlauf = d.durchlaufende_posten || {};
  const uebrigeSaldo = d.uebrige_salden || {};
  const kennzahlen = d.kennzahlen || {};

  // KPIs
  const kpiEinnahmen = euer.einnahmen ?? d.summe_einnahmen ?? null;
  const kpiAusgaben = euer.ausgaben ?? d.summe_ausgaben ?? null;
  const kpiUeberschuss = euer.ueberschuss ?? null;
  const kpiBanksaldo = banksaldo.saldo_ende ?? null;
  const einzugsquote = kennzahlen.einzugsquote_prozent ?? ((zmGesamt.soll > 0) ? (zmGesamt.zahlung / zmGesamt.soll * 100) : null);

  // Summen
  const summeUmlage = d.summe_umlagefaehig ?? null;
  const summeNichtUmlage = d.summe_nicht_umlagefaehig ?? null;
  const darlehenSumme = d.darlehen_summe ?? null;

  return (
    <div className="objreport">
      {/* ── Header ── */}
      <div className="objreport__header">
        <h1 className="objreport__title">Eigent&uuml;mer-Report</h1>
        <p className="objreport__subtitle">Objekt ausw&auml;hlen und Report aus Immoware24 laden</p>
      </div>

      {/* ── Objekt-Auswahl ── */}
      {!data && !loading && (
        <div className="objreport__selector-wrap">
          <ObjektSelector token={token} selected={selectedObjekt} onSelect={id => setSelectedObjekt(id)} disabled={loading} />
          <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button onClick={loadReport} disabled={!selectedObjekt || loading}
              className="objreport__btn objreport__btn--primary">
              Report laden
            </button>
            <button onClick={downloadHtmlReport} disabled={!data}
              className="objreport__btn objreport__btn--secondary">
              HTML Report herunterladen
            </button>
            {error && <div className="objreport__error">{error}</div>}
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && <LoadingOverlay />}

      {/* ══════════ REPORT DASHBOARD ══════════ */}
      {data && !loading && (
        <div className="objreport__dashboard">

          {/* Top Bar */}
          <div className="objreport__topbar">
            <button className="objreport__btn objreport__btn--secondary"
              onClick={() => { setData(null); setSelectedObjekt(null); }}>
              &larr; Zur&uuml;ck
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="objreport__btn objreport__btn--secondary" onClick={downloadHtmlReport}>
                HTML Report herunterladen
              </button>
              <button className="objreport__btn objreport__btn--secondary" onClick={() => window.print()}>
                PDF speichern
              </button>
            </div>
          </div>

          {/* Meta / Objekt-Info */}
          <div className="objreport__meta">
            <div>
              {meta.objekt && <div className="objreport__meta-objekt">Objekt {meta.objekt}</div>}
              {meta.eigentuemer && <div className="objreport__meta-eigen">Eigent&uuml;mer: {meta.eigentuemer}</div>}
            </div>
            <div className="objreport__meta-zeitraum">
              {meta.zeitraum || `${meta.zeitraum_von || ''} \u2013 ${meta.zeitraum_bis || ''}`}
            </div>
          </div>

          {/* ── KPI Cards ── */}
          <div className="objreport__kpi-grid">
            {kpiEinnahmen != null && (
              <div className="objreport__kpi objreport__kpi--success">
                <div className="objreport__kpi-label">Einnahmen</div>
                <div className="objreport__kpi-value">{eur(kpiEinnahmen)}</div>
                <div className="objreport__kpi-sub">Mieten + NK-Vorauszahlung</div>
              </div>
            )}
            {kpiAusgaben != null && (
              <div className="objreport__kpi objreport__kpi--danger">
                <div className="objreport__kpi-label">Ausgaben</div>
                <div className="objreport__kpi-value">{eur(kpiAusgaben)}</div>
                <div className="objreport__kpi-sub">umlagef. + nicht umlagef.</div>
              </div>
            )}
            {kpiUeberschuss != null && (
              <div className={`objreport__kpi ${kpiUeberschuss >= 0 ? 'objreport__kpi--success' : 'objreport__kpi--danger'}`}>
                <div className="objreport__kpi-label">&Uuml;berschuss</div>
                <div className="objreport__kpi-value">{eur(kpiUeberschuss)}</div>
                <div className="objreport__kpi-sub">nach Ausgaben, vor Darlehen</div>
              </div>
            )}
            {kpiBanksaldo != null && (
              <div className="objreport__kpi">
                <div className="objreport__kpi-label">Banksaldo</div>
                <div className="objreport__kpi-value">{eur(kpiBanksaldo)}</div>
                <div className="objreport__kpi-sub">Endsaldo Objektkonto</div>
              </div>
            )}
            {einzugsquote != null && (
              <div className={`objreport__kpi ${einzugsquote >= 100 ? 'objreport__kpi--success' : einzugsquote >= 95 ? 'objreport__kpi--warning' : 'objreport__kpi--danger'}`}>
                <div className="objreport__kpi-label">Einzugsquote</div>
                <div className="objreport__kpi-value">
                  {Number(einzugsquote).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                </div>
                <div className="objreport__kpi-sub">Zahlungseingang Mieter</div>
              </div>
            )}
          </div>

          {/* ── Zahlungen Mieter ── */}
          {(zmGesamt.soll != null || zm.soll != null) && (
            <Section icon="\ud83c\udfe0" title="Zahlungen Mieter" badge={zm.anzahl_mieter != null ? `${zm.anzahl_mieter} Einheiten` : null}>
              <div className="objreport__grid-4">
                {(zmGesamt.saldovortrag ?? zm.saldovortrag) != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Saldovortrag</div>
                    <div className="objreport__stat-value">{eur(zmGesamt.saldovortrag ?? zm.saldovortrag)}</div>
                  </div>
                )}
                {(zmGesamt.soll ?? zm.soll) != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">SOLL</div>
                    <div className="objreport__stat-value">{eur(zmGesamt.soll ?? zm.soll)}</div>
                  </div>
                )}
                {(zmGesamt.zahlung ?? zm.zahlung) != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Zahlung (IST)</div>
                    <div className="objreport__stat-value" style={{ color: 'var(--status-success)' }}>
                      {eur(zmGesamt.zahlung ?? zm.zahlung)}
                    </div>
                  </div>
                )}
                {(zmGesamt.saldo ?? zm.saldo) != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Saldo</div>
                    <div className="objreport__stat-value" style={{
                      color: (zmGesamt.saldo ?? zm.saldo ?? 0) > 0 ? 'var(--status-danger)' : 'var(--status-success)'
                    }}>{eur(zmGesamt.saldo ?? zm.saldo)}</div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* ── Einnahmen ── */}
          {einnahmen.length > 0 && (
            <Section icon="\ud83d\udcc8" title="Einnahmen (E\u00dcR)" badge={kpiEinnahmen != null ? eur(kpiEinnahmen) : null}>
              <KontenTabelle konten={einnahmen} summe={kpiEinnahmen} summeLabel="Summe Einnahmen" />
            </Section>
          )}

          {/* ── Ausgaben umlagefaehig ── */}
          {ausgabenUmlage.length > 0 && (
            <Section icon="\ud83d\udcc9" title="Ausgaben umlagef\u00e4hig" badge={summeUmlage != null ? eur(summeUmlage) : null}>
              <KontenTabelle konten={ausgabenUmlage} summe={summeUmlage} summeLabel="Summe umlagef\u00e4hig" />
            </Section>
          )}

          {/* ── Ausgaben nicht umlagefaehig ── */}
          {ausgabenNichtUmlage.length > 0 && (
            <Section icon="\ud83d\udcc9" title="Ausgaben nicht umlagef\u00e4hig" badge={summeNichtUmlage != null ? eur(summeNichtUmlage) : null}>
              <KontenTabelle konten={ausgabenNichtUmlage} summe={summeNichtUmlage} summeLabel="Summe nicht umlagef\u00e4hig" />
            </Section>
          )}

          {/* ── Darlehen ── */}
          {darlehen.length > 0 && (
            <Section icon="\ud83c\udfe6" title="Darlehen" badge={darlehenSumme != null ? eur(darlehenSumme) : null}>
              <KontenTabelle konten={darlehen} summe={darlehenSumme} summeLabel="Summe Darlehen" />
            </Section>
          )}

          {/* ── Banksaldo-Entwicklung ── */}
          {(banksaldo.saldo_beginn != null || banksaldo.saldo_ende != null) && (
            <Section icon="\ud83c\udfe6" title="Banksaldo-Entwicklung">
              <div className="objreport__bank-grid">
                <div className="objreport__stat">
                  <div className="objreport__stat-label">Anfangssaldo</div>
                  <div className="objreport__stat-value">{eur(banksaldo.saldo_beginn)}</div>
                </div>
                {banksaldo.einnahme_ueberschuss != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">+ Einnahme-&Uuml;berschuss</div>
                    <div className="objreport__stat-value">{eur(banksaldo.einnahme_ueberschuss)}</div>
                  </div>
                )}
                {entnahmen.zufuehrung != null && entnahmen.zufuehrung !== 0 && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">+ Einlagen</div>
                    <div className="objreport__stat-value" style={{ color: 'var(--status-success)' }}>{eur(entnahmen.zufuehrung)}</div>
                  </div>
                )}
                {entnahmen.auszahlung != null && entnahmen.auszahlung !== 0 && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">&minus; Auszahlungen</div>
                    <div className="objreport__stat-value" style={{ color: 'var(--status-danger)' }}>{eur(Math.abs(entnahmen.auszahlung))}</div>
                  </div>
                )}
                {darlehenSumme != null && darlehenSumme !== 0 && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">&minus; Darlehen</div>
                    <div className="objreport__stat-value">{eur(Math.abs(darlehenSumme))}</div>
                  </div>
                )}
                {banksaldo.sonstige_einnahmen_ausgaben != null && banksaldo.sonstige_einnahmen_ausgaben !== 0 && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Sonstige</div>
                    <div className="objreport__stat-value">{eur(banksaldo.sonstige_einnahmen_ausgaben)}</div>
                  </div>
                )}
                <div className="objreport__stat objreport__stat--highlight">
                  <div className="objreport__stat-label">Endsaldo</div>
                  <div className="objreport__stat-value">{eur(banksaldo.saldo_ende)}</div>
                </div>
              </div>
              {/* Wasserfall-Visualisierung */}
              {banksaldo.saldo_beginn != null && banksaldo.saldo_ende != null && (
                <div style={{ marginTop: '1rem' }}>
                  <WasserfallBar label="Anfangssaldo" value={banksaldo.saldo_beginn} max={Math.max(Math.abs(banksaldo.saldo_beginn), Math.abs(banksaldo.saldo_ende))} color="var(--text-secondary)" />
                  <WasserfallBar label="Endsaldo" value={banksaldo.saldo_ende} max={Math.max(Math.abs(banksaldo.saldo_beginn), Math.abs(banksaldo.saldo_ende))} color="var(--accent)" />
                </div>
              )}
            </Section>
          )}

          {/* ── Entnahmen / Einlagen ── */}
          {(entnahmen.zufuehrung != null || entnahmen.auszahlung != null) && (
            <Section icon="\ud83d\udcb8" title="Entnahmen / Einlagen">
              <div className="objreport__grid-4">
                {entnahmen.zufuehrung != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Zuf&uuml;hrung</div>
                    <div className="objreport__stat-value" style={{ color: 'var(--status-success)' }}>{eur(entnahmen.zufuehrung)}</div>
                  </div>
                )}
                {entnahmen.auszahlung != null && (
                  <div className="objreport__stat">
                    <div className="objreport__stat-label">Auszahlung</div>
                    <div className="objreport__stat-value" style={{ color: 'var(--status-danger)' }}>{eur(entnahmen.auszahlung)}</div>
                  </div>
                )}
                {entnahmen.saldo != null && (
                  <div className="objreport__stat objreport__stat--highlight">
                    <div className="objreport__stat-label">Saldo</div>
                    <div className="objreport__stat-value">{eur(entnahmen.saldo)}</div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* ── Footer ── */}
          <div className="objreport__footer">
            <span>One. immo &middot; Hamburg</span>
            <span className="objreport__footer-note">
              {meta.erstellt_am
                ? `Erstellt auf Basis der Immoware24-Abrechnung vom ${meta.erstellt_am}`
                : 'Erstellt auf Basis der Immoware24-Abrechnung'}
            </span>
          </div>

        </div>
      )}
    </div>
  );
}
